import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import { adminGetSettings, adminPutSettings } from "@/lib/api";
import { adminInputCls, adminGoldBtnCls } from "./shared";

export const BusinessTab = ({ onAuthError }: { onAuthError: () => void }) => {
  const [biz, setBiz] = useState<any>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminGetSettings()
      .then((r) => {
        const b = r.data.business || {};
        setBiz({
          contact_name: b.contact_name || "",
          phone: b.phone || "",
          email: b.email || "",
          r3_phone2: b.r3_phone2 || "",
          services_drywall: (b.services_drywall || []).join(", "),
          services_r3: (b.services_r3 || []).join(", "),
        });
      })
      .catch((e) => {
        if (e?.response?.status === 401) onAuthError();
        else setBiz({});
      });
  }, [onAuthError]);

  const save = async () => {
    setSaving(true);
    try {
      await adminPutSettings({
        business: {
          contact_name: biz.contact_name,
          phone: biz.phone,
          email: biz.email,
          r3_phone2: biz.r3_phone2,
          services_drywall: biz.services_drywall.split(",").map((s: string) => s.trim()).filter(Boolean),
          services_r3: biz.services_r3.split(",").map((s: string) => s.trim()).filter(Boolean),
        },
      });
      toast.success("Business information saved — public site and AI assistant updated");
    } catch (e: any) {
      if (e?.response?.status === 401) onAuthError();
      else toast.error("Save failed");
    }
    setSaving(false);
  };

  if (!biz) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-gold" />
      </div>
    );
  }

  const field = (label: string, key: string, testId: string) => (
    <div>
      <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.2em] text-neutral-500">
        {label}
      </label>
      <input
        data-testid={testId}
        value={biz[key] || ""}
        onChange={(e) => setBiz((b: any) => ({ ...b, [key]: e.target.value }))}
        className={adminInputCls}
      />
    </div>
  );

  return (
    <div data-testid="admin-business-tab">
      <p className="text-sm text-neutral-400">
        Contact details and service lists shown across the public website and used by the Ask R3
        assistant. City names and phone numbers are shared between English and Spanish — no
        duplicate entries needed.
      </p>

      <div className="mt-6 border border-white/10 bg-charcoal p-6">
        <p className="font-display text-lg font-black uppercase tracking-tight text-white">
          R3Stan <span className="text-gold">Drywall, Inc.</span>
        </p>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          {field("Contact name", "contact_name", "biz-contact-name")}
          {field("Phone", "phone", "biz-phone")}
          {field("Email", "email", "biz-email")}
          {field("R3 Services second phone", "r3_phone2", "biz-phone2")}
        </div>
        <div className="mt-4">
          <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.2em] text-neutral-500">
            Drywall services (comma-separated)
          </label>
          <textarea
            data-testid="biz-services-drywall"
            rows={2}
            value={biz.services_drywall}
            onChange={(e) => setBiz((b: any) => ({ ...b, services_drywall: e.target.value }))}
            className={`${adminInputCls} resize-none`}
          />
        </div>
        <div className="mt-4">
          <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.2em] text-neutral-500">
            R3 Services (comma-separated)
          </label>
          <textarea
            data-testid="biz-services-r3"
            rows={2}
            value={biz.services_r3}
            onChange={(e) => setBiz((b: any) => ({ ...b, services_r3: e.target.value }))}
            className={`${adminInputCls} resize-none`}
          />
        </div>
        <button data-testid="biz-save-btn" onClick={save} disabled={saving} className={`${adminGoldBtnCls} mt-6`}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save Business Information
        </button>
      </div>
    </div>
  );
};
